import { Colors } from '@factorialco/deprecated-design-system'
import { BarDatum } from '@nivo/bar/dist/types/types'

import i18n from 'modules/core/lib/i18n'

import { formatGraphTimeTitle } from 'modules/project_management/lib/formatChartComponents'
import { chartDataTarget } from 'modules/project_management/lib/types'

const COLORS: Colors[] = [
  'viridian1000',
  'purple1000',
  'champagne1000',
  'fuchsia1000',
  'lime1000',
  'malibu1000',
  'tangerine1000',
  'viridian200',
  'purple200',
  'champagne200',
  'fuchsia200',
  'lime200',
  'malibu200',
  'tangerine200',
]

const OTHERS_KEY = '_others_'
const OTHERS_COLOR: Colors = 'grey300'

export type DetailTransformedData = {
  id: string
  label: string
  keyColors: { [key: string]: Colors }
  keys: string[]
  data: BarDatum[]
}

type TempDetailGraphData = { [key: string]: BarDatum }

export type TimeDetailGraph = {
  year: string
  timeSlot: string
  imputedMinutes?: number | null | undefined
  employeeName?: string | null | undefined
  subprojectName?: string | null | undefined
  projectName?: string | null | undefined
}

export type ProjectCostDistributionChart = {
  year: string
  timeSlot: string
  costCents?: number | null | undefined
  employeeName?: string | null | undefined
  subprojectName?: string | null | undefined
}

const cleanKeys = (arr: (string | null | undefined)[]) => {
  const unique: string[] = []
  arr.forEach((element) => {
    if (element !== null && element !== undefined && !unique.includes(element)) {
      unique.push(element)
    }
  })

  return sortOthers(unique)
}

function sortOthers(array: string[]) {
  const index = array.indexOf(OTHERS_KEY)

  if (index !== -1) {
    array.splice(index, 1)
    array.push(i18n.t('project_management.graphs.details.others'))
  }
  return array
}

const associateColors = (keys: string[]) => {
  const association: { [key: string]: Colors } = {}

  keys.forEach((item, i) => {
    if (item === i18n.t('project_management.graphs.details.others')) {
      association[item] = OTHERS_COLOR
      return
    }

    const c = COLORS[i % COLORS.length]
    if (c) association[item] = c
  })

  return association
}

export const getDetailTransformedData = (
  data: TimeDetailGraph[],
  dataType: chartDataTarget,
  slotType: string
) => {
  const keys = cleanKeys(
    data.map((element) => {
      switch (dataType) {
        case 'employees':
          return element.employeeName
        case 'subprojects':
          return element.subprojectName
        case 'projects':
          return element.projectName
      }
    })
  )

  const generateData = () => {
    const hashResult = data.reduce(
      (result: TempDetailGraphData, currentObject: TimeDetailGraph) => {
        const key = `${currentObject.year}-${currentObject.timeSlot}`

        if (!result[key]) {
          result[key] = {
            year: currentObject.year,
            timeSlot: currentObject.timeSlot,
            label: formatGraphTimeTitle(currentObject.year, currentObject.timeSlot, slotType),
          }
        }

        const getName = () => {
          switch (dataType) {
            case 'employees':
              return currentObject.employeeName
            case 'subprojects':
              return currentObject.subprojectName
            case 'projects':
              return currentObject.projectName
          }
        }
        const name = getName()

        if (name) {
          const newName =
            name === OTHERS_KEY ? i18n.t('project_management.graphs.details.others') : name

          result[key][newName] = ((currentObject.imputedMinutes || 0) / 60).toFixed(2)
        }

        return result
      },
      {}
    )

    return Object.values(hashResult)
  }

  const transformedData: DetailTransformedData = {
    id: i18n.t('project_management.graphs.evolution.time.legends.registered_hours'),
    label: i18n.t('project_management.graphs.evolution.time.legends.registered_hours'),
    keys: keys,
    keyColors: associateColors(keys),
    data: generateData(),
  }

  return transformedData
}

export const getCostDistributionTransformedData = (
  data: ProjectCostDistributionChart[],
  dataType: chartDataTarget,
  slotType: string
) => {
  const keys = cleanKeys(
    data.map((element) =>
      dataType === 'employees' ? element.employeeName : element.subprojectName
    )
  )

  const generateData = () => {
    const hashResult = data.reduce(
      (result: TempDetailGraphData, currentObject: ProjectCostDistributionChart) => {
        const key = `${currentObject.year}-${currentObject.timeSlot}`

        if (!result[key]) {
          result[key] = {
            year: currentObject.year,
            timeSlot: currentObject.timeSlot,
            label: formatGraphTimeTitle(currentObject.year, currentObject.timeSlot, slotType),
          }
        }

        const name =
          dataType === 'employees' ? currentObject.employeeName : currentObject.subprojectName

        if (name) {
          const newName =
            name === OTHERS_KEY ? i18n.t('project_management.graphs.details.others') : name

          result[key][newName] = Math.round((currentObject.costCents || 0) * 100) / 10000
        }

        return result
      },
      {}
    )

    return Object.values(hashResult)
  }

  const transformedData: DetailTransformedData = {
    id: i18n.t('project_management.graphs.evolution.cost.legends.registered_cost'),
    label: i18n.t('project_management.graphs.evolution.cost.legends.registered_cost'),
    keys: keys,
    keyColors: associateColors(keys),
    data: generateData(),
  }

  return transformedData
}
